import {computed, inject, Injectable, Signal} from '@angular/core';
import {CompanySettingsStore} from '@company-settings/data-access';
import {UserPreferDateFormatType} from '@core/api/data-access';
import {MainInjector} from '@shared-types';
import dayjs from 'dayjs';
import 'dayjs/locale/he';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import {DateTime} from '../logic';
import {ActiveUserStore} from '@active-user-management';
import adaptDateFormatToDayjs = DateTime.DateTimeUnitTransformer.adaptDateFormatToDayjs;

@Injectable({
	providedIn: 'root',
})
export class DateTimeService {
	private readonly activeUserStore: ActiveUserStore = inject(ActiveUserStore);
	private readonly companySettingsStore = inject(CompanySettingsStore);
	public readonly defaultDateFormat: Signal<UserPreferDateFormatType> = computed(() => {
		return this.activeUserStore.user$()?.preferences?.dateFormat || this.companySettingsStore.state().settings?.defaultDateFormat;
	});

	constructor() {
		dayjs.extend(utc);
		dayjs.extend(timezone);
		dayjs.extend(relativeTime);
	}

	public formatDate(date: dayjs.ConfigType, appendTimeFormat: '' | TimeFormat = '', format: string = this.defaultDateFormat()?.toString()): string {
		const localDate = dayjs.utc(date)?.tz(dayjs.tz.guess());

		if (appendTimeFormat) {
			format += ` ${appendTimeFormat}`;
		}
		format = adaptDateFormatToDayjs(format);
		return localDate.format(format);
	}

	/**
	 * Returns a string representing the relative time from now (without suffix).
	 * @param waitingTime The time to compare with the current time.
	 * @param locale
	 * @returns The relative time from now as a string, without a suffix.
	 */
	public fromNow(waitingTime: dayjs.ConfigType, locale: 'he' | 'en' = 'he'): string {
		return dayjs.utc(waitingTime).tz(dayjs.tz.guess()).locale(locale).fromNow();
	}
}

export function getDateTimeService(): DateTimeService {
	return MainInjector.get(DateTimeService);
}
